export interface WidgetScopeOptions {
  publicKey?: string;
  accountUuid?: string;
  companyCode?: string;
}

export interface WidgetScopedEventDetail {
  instanceId?: string;
  publicKey?: string;
  [key: string]: unknown;
}

const MAX_SCOPE_SEGMENT_LENGTH = 64;

export const sanitizeWidgetScopePart = ( value: unknown ): string => {
  const normalized = String( value || '' )
    .trim()
    .toLowerCase()
    .replace( /[^a-z0-9_-]+/g, '-' )
    .replace( /-+/g, '-' )
    .replace( /^-+|-+$/g, '' );

  return normalized.slice( 0, MAX_SCOPE_SEGMENT_LENGTH );
};

export const buildWidgetInstanceId = ( {
  publicKey,
  accountUuid,
  companyCode,
}: WidgetScopeOptions ): string => {
  const parts = [
    sanitizeWidgetScopePart( publicKey ),
    sanitizeWidgetScopePart( accountUuid ),
    sanitizeWidgetScopePart( companyCode ),
  ].filter( Boolean );

  if( parts.length === 0 )
    return 'hmw-default';

  return `hmw-${parts.join( '--' )}`;
};

export const buildWidgetLegalCookieName = ( scope: WidgetScopeOptions ): string =>
  `hellomatik-legal-${buildWidgetInstanceId( scope )}`;

export const isEventForWidget = (
  event: Event,
  instanceId: string,
  publicKey?: string
): boolean => {
  if( !( event instanceof CustomEvent ) )
    return true;

  const detail = event.detail;
  if( !detail || typeof detail !== 'object' )
    return true;

  const eventDetail = detail as WidgetScopedEventDetail;
  if( typeof eventDetail.instanceId === 'string' && eventDetail.instanceId.trim() !== '' )
    return eventDetail.instanceId === instanceId;

  if( typeof eventDetail.publicKey === 'string' && eventDetail.publicKey.trim() !== '' )
    return !publicKey || eventDetail.publicKey === publicKey;

  return true;
};

export const buildWidgetEventDetail = (
  instanceId: string,
  publicKey?: string,
  detail: Record<string, unknown> = {}
): WidgetScopedEventDetail => ( {
  ...detail,
  instanceId,
  ...( publicKey ? { publicKey } : {} ),
} );
